<script setup>
import { computed, onMounted, ref } from 'vue'
import creativeUploadAlertService from '@/services/creativeUploadAlertService'

const alerts = ref([])
const loading = ref(false)

const alertCount = computed(() => alerts.value.length)

const loadAlerts = async () => {
  loading.value = true
  try {
    const response = await creativeUploadAlertService.getUnread()
    alerts.value = response.data || []
  } catch {
    alerts.value = []
  } finally {
    loading.value = false
  }
}

const markRead = async (alert) => {
  try {
    await creativeUploadAlertService.markRead(alert.id)
    alerts.value = alerts.value.filter((item) => item.id !== alert.id)
  } catch {
    await loadAlerts()
  }
}

const titleFor = (alert) => {
  const parts = [
    alert.network,
    alert.creative_type,
    alert.filename || (alert.creative_id ? `ID ${alert.creative_id}` : null),
  ].filter(Boolean)

  return parts.join(' / ')
}

const reasonFor = (alert) => {
  return alert.reason || alert.error_code || alert.status || 'Rejected'
}

onMounted(loadAlerts)
</script>

<template>
  <CDropdown placement="bottom-end" variant="nav-item">
    <CDropdownToggle :caret="false" class="position-relative">
      <CIcon icon="cil-bell" size="lg" />
      <CBadge v-if="alertCount" color="danger" position="top-end" shape="rounded-pill">
        {{ alertCount }}
      </CBadge>
    </CDropdownToggle>
    <CDropdownMenu class="creative-alerts-menu">
      <CDropdownHeader component="h6" class="bg-body-secondary text-body-secondary fw-semibold mb-2">
        Creative rejects
      </CDropdownHeader>
      <CDropdownItem v-if="loading" disabled>Loading...</CDropdownItem>
      <CDropdownItem v-else-if="!alerts.length" disabled>No rejects</CDropdownItem>
      <div v-else class="creative-alerts-list">
        <div v-for="alert in alerts" :key="alert.id" class="creative-alert-item">
          <div class="creative-alert-body">
            <div class="creative-alert-title">{{ titleFor(alert) }}</div>
            <div class="creative-alert-reason">{{ reasonFor(alert) }}</div>
            <div v-if="alert.error_code" class="creative-alert-code">Code: {{ alert.error_code }}</div>
          </div>
          <button class="creative-alert-close" type="button" @click.stop="markRead(alert)">x</button>
        </div>
      </div>
    </CDropdownMenu>
  </CDropdown>
</template>

<!-- Not scoped on purpose: CDropdownMenu sets inheritAttrs: false and renders through
     a conditional teleport, so the scope attribute never reaches the menu even though
     the class does. Every selector below is prefixed with creative-alert. -->
<style>
.creative-alerts-menu {
  /* Wide enough for a network's own error text, which is what the reason
     line usually carries. */
  width: min(960px, calc(100vw - 24px));
  max-height: 620px;
  overflow: hidden;
}

.creative-alerts-list {
  max-height: 540px;
  overflow-y: auto;
}

.creative-alert-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 14px;
  border-bottom: 1px solid var(--cui-border-color);
}

.creative-alert-body {
  min-width: 0;
  flex: 1;
}

.creative-alert-title {
  font-weight: 600;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.creative-alert-reason,
.creative-alert-code {
  margin-top: 4px;
  font-size: 12px;
  color: var(--cui-secondary-color);
  overflow-wrap: anywhere;
}

.creative-alert-close {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--cui-secondary-color);
  line-height: 1;
}

.creative-alert-close:hover {
  background: var(--cui-tertiary-bg);
  color: var(--cui-body-color);
}
</style>
